import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SEO from '../../components/SEO';
import PublicHeader from '../../components/PublicHeader';
import PublicFooter from '../../components/PublicFooter';
import './PublicPages.css';

export default function PricingPage() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="public-page">
      <SEO
        title="Hangrily Pricing — Free Trial + $9.99/mo Unlimited Restaurant Decisions"
        description="Hangrily pricing: try free with no account needed. Unlimited restaurant decision sessions for solo, couples & groups at $9.99/month — plus $1 cashback on every order (up to $10/mo). Compare plans, cancel anytime. No hidden fees."
        path="/pricing"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: 'Hangrily Pricing Plans',
            description: 'Compare Hangrily free trial vs premium subscription plans for restaurant decision sessions.',
            url: 'https://hangrily.app/pricing'
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://hangrily.app' },
              { '@type': 'ListItem', position: 2, name: 'Pricing', item: 'https://hangrily.app/pricing' }
            ]
          },
          {
            '@context': 'https://schema.org',
            '@type': 'Product',
            name: 'Hangrily Premium',
            description: 'Unlimited restaurant decision sessions for solo, couples, and groups.',
            brand: { '@type': 'Brand', name: 'Hangrily' },
            offers: [
              {
                '@type': 'Offer',
                name: 'Free Trial',
                price: '0',
                priceCurrency: 'USD',
                availability: 'https://schema.org/InStock',
                description: 'Try Hangrily free — no account required'
              },
              {
                '@type': 'Offer',
                name: 'Premium Monthly',
                price: '9.99',
                priceCurrency: 'USD',
                availability: 'https://schema.org/InStock',
                priceValidUntil: '2027-12-31',
                description: 'Unlimited sessions, all modes, full analytics'
              }
            ],
            aggregateRating: {
              '@type': 'AggregateRating',
              ratingValue: '4.8',
              ratingCount: '1250',
              bestRating: '5'
            }
          },
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: 'How much does Hangrily cost?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Hangrily offers a free trial with no account required. After the trial, unlimited access is $9.99 per month. There are no hidden fees, and you can cancel anytime.'
                }
              },
              {
                '@type': 'Question',
                name: 'Is there a free version of Hangrily?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Yes! You can try Hangrily completely free without creating an account. Guest users get 3 free sessions per week across solo, couple, and group modes.'
                }
              },
              {
                '@type': 'Question',
                name: 'Can I cancel my Hangrily subscription?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Absolutely. Cancel anytime from your profile settings. No cancellation fees, no questions asked. Your access continues until the end of your current billing period.'
                }
              },
              {
                '@type': 'Question',
                name: 'Is Hangrily worth it compared to free restaurant pickers?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Free pickers like wheel spinners are purely random — they don\'t consider your preferences, location, or group consensus. Hangrily uses smart matching with real restaurant data, photos, and ratings. At $9.99/month, most users say it pays for itself the first week by ending dinner arguments.'
                }
              }
            ]
          }
        ]}
      />
      <PublicHeader />

      <main className="page-content">
        <section className="page-hero">
          <h1>Hangrily Pricing</h1>
          <p className="hero-subtitle">
            End restaurant indecision for less than a coffee. Try free — no account needed.
          </p>
        </section>

        <section className="content-section">
          <div className="comparison-grid" style={{ maxWidth: '900px', margin: '0 auto' }}>
            <div className="comparison-item" style={{ border: '2px solid rgba(255, 255, 255, 0.1)', borderRadius: '16px', padding: '32px', textAlign: 'center' }}>
              <h3 style={{ fontSize: '1.5rem', marginBottom: '8px' }}>🆓 Free Trial</h3>
              <div style={{ fontSize: '3rem', fontWeight: '800', color: '#FF6347', margin: '16px 0' }}>$0</div>
              <p style={{ color: 'rgba(255,255,255,0.6)', marginBottom: '24px' }}>No account required</p>
              <ul style={{ textAlign: 'left', listStyle: 'none', padding: 0, marginBottom: '24px' }}>
                <li style={{ padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>✅ 3 sessions per week</li>
                <li style={{ padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>✅ Solo, Couple & Group modes</li>
                <li style={{ padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>✅ Real restaurant data & photos</li>
                <li style={{ padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>✅ Location-based search</li>
                <li style={{ padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>✅ Cuisine & price filters</li>
                <li style={{ padding: '8px 0', color: 'rgba(255,255,255,0.3)' }}>❌ Decision history & analytics</li>
                <li style={{ padding: '8px 0', color: 'rgba(255,255,255,0.3)' }}>❌ Favorites & custom lists</li>
                <li style={{ padding: '8px 0', color: 'rgba(255,255,255,0.3)' }}>❌ Rewards & points</li>
                <li style={{ padding: '8px 0', color: 'rgba(255,255,255,0.3)' }}>❌ Cashback on orders</li>
              </ul>
              <button className="btn btn-secondary btn-lg" onClick={() => navigate('/try')} style={{ width: '100%' }}>
                Try Free Now
              </button>
            </div>

            <div className="comparison-item win" style={{ border: '2px solid #FF6347', borderRadius: '16px', padding: '32px', textAlign: 'center', position: 'relative' }}>
              <div style={{ position: 'absolute', top: '-14px', left: '50%', transform: 'translateX(-50%)', background: '#FF6347', padding: '4px 16px', borderRadius: '20px', fontSize: '0.85rem', fontWeight: '700' }}>MOST POPULAR</div>
              <h3 style={{ fontSize: '1.5rem', marginBottom: '8px' }}>⭐ Premium</h3>
              <div style={{ fontSize: '3rem', fontWeight: '800', color: '#FF6347', margin: '16px 0' }}>$9.99<span style={{ fontSize: '1rem', fontWeight: '400', color: 'rgba(255,255,255,0.6)' }}>/month</span></div>
              <p style={{ color: 'rgba(255,255,255,0.6)', marginBottom: '24px' }}>Cancel anytime</p>
              <ul style={{ textAlign: 'left', listStyle: 'none', padding: 0, marginBottom: '24px' }}>
                <li style={{ padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>✅ <strong>$1 cashback per order</strong> (up to 10/mo)</li>
                <li style={{ padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>✅ <strong>Unlimited</strong> sessions</li>
                <li style={{ padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>✅ Solo, Couple & Group modes</li>
                <li style={{ padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>✅ Real restaurant data & photos</li>
                <li style={{ padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>✅ Location-based search</li>
                <li style={{ padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>✅ Cuisine & price filters</li>
                <li style={{ padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>✅ <strong>Decision history & analytics</strong></li>
                <li style={{ padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>✅ <strong>Favorites & custom lists</strong></li>
                <li style={{ padding: '8px 0' }}>✅ <strong>Rewards, points & 2x multiplier</strong></li>
              </ul>
              <button className="btn btn-primary btn-lg" onClick={() => navigate('/register')} style={{ width: '100%' }}>
                Start Free Trial →
              </button>
            </div>
          </div>
        </section>

        <section className="content-section alt-bg">
          <h2 className="section-title">Why $9.99/Month Is a No-Brainer</h2>
          <div className="reason-cards">
            <div className="reason-card">
              <div className="reason-icon">💰</div>
              <h3>It Can Pay For Itself</h3>
              <p>Premium members earn $1 cashback on every verified order — up to $10 a month. Order takeout a few times a week and the cashback nearly covers the $9.99 subscription.</p>
            </div>
            <div className="reason-card">
              <div className="reason-icon">⏱️</div>
              <h3>Save 30+ Minutes Weekly</h3>
              <p>The average couple spends 30 minutes per meal deciding where to eat. With Hangrily, it's 2 minutes. That's hours of your life back every month.</p>
            </div>
            <div className="reason-card">
              <div className="reason-icon">😊</div>
              <h3>Zero Arguments</h3>
              <p>No more "I don't care, you pick" spirals. Everyone swipes, the app decides. Happy meals, happy relationships. Can you really put a price on that?</p>
            </div>
          </div>
        </section>

        <section className="content-section">
          <h2 className="section-title">Hangrily vs. Free Alternatives</h2>
          <div className="comparison-grid">
            <div className="comparison-item lose">
              <h3>❌ Google Maps / Yelp</h3>
              <p>Free but overwhelming. 50+ options, analysis paralysis, no group voting. You spend 30 minutes scrolling and still can't decide.</p>
            </div>
            <div className="comparison-item lose">
              <h3>❌ Wheel Spinners</h3>
              <p>Free and random. Zero preference matching, no photos or ratings, doesn't work for groups. You might get fast food when you wanted Italian.</p>
            </div>
            <div className="comparison-item lose">
              <h3>❌ "Just Pick Something"</h3>
              <p>Free but costly to relationships. Leads to vetoes, arguments, and settling for the same place every time. Not a real solution.</p>
            </div>
            <div className="comparison-item win">
              <h3>✅ Hangrily Premium</h3>
              <p>$9.99/mo for smart matching, real restaurant data, couple & group modes, analytics, and zero dinner arguments. Worth every penny.</p>
            </div>
          </div>
        </section>

        <section className="content-section alt-bg">
          <h2 className="section-title">Frequently Asked Questions</h2>
          <div className="faq-grid">
            <div className="faq-item">
              <h3>How much does Hangrily cost?</h3>
              <p>Hangrily offers a free trial with no account required. After the trial, unlimited access is $9.99 per month. There are no hidden fees, and you can cancel anytime.</p>
            </div>
            <div className="faq-item">
              <h3>Is there a free version of Hangrily?</h3>
              <p>Yes! You can try Hangrily completely free without creating an account. Guest users get 3 free sessions per week across solo, couple, and group modes.</p>
            </div>
            <div className="faq-item">
              <h3>Can I cancel anytime?</h3>
              <p>Absolutely. Cancel anytime from your profile settings. No cancellation fees, no questions asked. Your access continues until the end of your current billing period.</p>
            </div>
            <div className="faq-item">
              <h3>Is Hangrily worth it vs free pickers?</h3>
              <p>Free pickers are purely random. Hangrily uses smart matching with real restaurant data. At $9.99/month, most users say it pays for itself the first week.</p>
            </div>
          </div>
        </section>

        <section className="cta-banner">
          <h2>Try Hangrily Free — No Account Needed</h2>
          <p>See for yourself why thousands of couples and friend groups trust Hangrily for dinner decisions.</p>
          <button className="btn btn-primary btn-lg" onClick={() => navigate('/try')}>
            Start Free Session →
          </button>
        </section>
      </main>

      <PublicFooter />
    </div>
  );
}
